import React, { createContext, useContext, useEffect, useMemo } from "react";

export const LANGS = [
  { param: "en", code: "en", label: "English", short: "EN" },
  { param: "zh-CN", code: "zh", label: "简体中文", short: "中文" },
];

export const DEFAULT_LANG = "en";
export const LANG_KEY = "lumora_lang";

export function paramToCode(param) {
  return param === "zh-CN" ? "zh" : "en";
}
export function isValidLangParam(p) {
  return p === "en" || p === "zh-CN";
}

const dict = {
  en: {
    "brand.tagline": "A Brighter Future Starts With Education",
    "brand.support": "Explore a brighter world through learning, creativity, and community-powered impact.",

    "nav.home": "Home",
    "nav.learn": "Learn",
    "nav.play": "Play",
    "nav.stories": "Stories",
    "nav.impact": "Our Impact",
    "nav.about": "About",
    "nav.token": "Token",
    "nav.community": "Community",
    "nav.faq": "FAQ",
    "nav.startLearning": "Start Learning",
    "nav.buyToken": "Buy Token",
    "nav.language": "Language",
    "nav.menu": "Menu",

    "common.startLesson": "Start Lesson",
    "common.continue": "Continue",
    "common.play": "Play",
    "common.playNow": "Play Now",
    "common.readStory": "Read Story",
    "common.search": "Search",
    "common.searchPlaceholder": "Search lessons…",
    "common.all": "All",
    "common.category": "Category",
    "common.difficulty": "Difficulty",
    "common.sortBy": "Sort by",
    "common.age": "Age",
    "common.duration": "Duration",
    "common.min": "min",
    "common.completed": "Completed",
    "common.inProgress": "In progress",
    "common.notStarted": "Not started",
    "common.back": "Back",
    "common.next": "Next",
    "common.previous": "Previous",
    "common.comingSoon": "Coming Soon",
    "common.notVerified": "Not Yet Verified",
    "common.notAvailable": "Not Yet Available",
    "common.learnMore": "Learn more",
    "common.viewAll": "View all",
    "common.noResults": "No results found. Try a different search or filter.",
    "common.restart": "Play Again",
    "common.score": "Score",
    "common.correct": "Correct!",
    "common.incorrect": "Not quite — try again!",
    "common.yourBest": "Your best",
    "common.difficultyEasy": "Easy",
    "common.difficultyMedium": "Medium",
    "common.difficultyHard": "Hard",
    "common.openInNewTab": "opens in a new tab",

    "home.hero.title": "A Brighter Future Starts With Education",
    "home.hero.subtitle": "Welcome to LUMORA — a magical world where children and families learn, play, and help build a kinder, brighter future together. Free to explore. No wallet required.",
    "home.cta.start": "Start Learning",
    "home.cta.impact": "Explore Our Impact",
    "home.philosophy.title": "Learn • Play • Give",
    "home.philosophy.learn": "Learn",
    "home.philosophy.learnDesc": "Engaging, accessible lessons across seven joyful subjects.",
    "home.philosophy.play": "Play",
    "home.philosophy.playDesc": "Interactive educational games that make learning a delight.",
    "home.philosophy.give": "Give",
    "home.philosophy.giveDesc": "Transparent, verifiable charitable contributions — no fake numbers.",
    "home.categories.title": "Featured Learning Categories",
    "home.categories.subtitle": "Pick a path and start exploring real, hands-on lessons.",
    "home.preview.title": "Peek inside a lesson",
    "home.preview.subtitle": "Every lesson has clear objectives, short readable sections, and a friendly knowledge check.",
    "home.games.title": "Featured Games",
    "home.games.subtitle": "Quick, playful challenges that build real skills.",
    "home.stories.title": "Featured Stories",
    "home.stories.subtitle": "Gentle tales that teach kindness, curiosity, and courage.",
    "home.community.title": "A Community-Powered Mission",
    "home.community.desc": "LUMORA grows through learners, families, and volunteers who believe education should be free and joyful for everyone.",
    "home.impact.title": "Transparent by Design",
    "home.impact.desc": "We show only what we can prove. Charitable contributions move through clear stages — Planned, Pending, and Confirmed — each traceable on-chain when live.",
    "home.giggle.title": "Supporting the Giggle Academy Mission",
    "home.giggle.desc": "LUMORA is an independent initiative inspired by the goal of making education more accessible. We aim to support Giggle Academy's educational mission through transparent charitable contributions, subject to recipient verification and the applicable donation process.",
    "home.resources.title": "Latest Educational Resources",
    "home.resources.subtitle": "Fresh lessons and stories to explore right now.",

    "learn.title": "Education Hub",
    "learn.subtitle": "A free, searchable library of hands-on lessons across seven subjects.",
    "learn.lessonsCount": "{n} lessons",
    "learn.sort.recommended": "Recommended",
    "learn.sort.shortest": "Shortest first",
    "learn.sort.az": "A to Z",
    "learn.objectives": "What you'll learn",

    "lesson.objectives": "Learning Objectives",
    "lesson.progress": "Progress",
    "lesson.check": "Knowledge Check",
    "lesson.checkPrompt": "Choose the best answer:",
    "lesson.markComplete": "Mark as Complete",
    "lesson.completedTitle": "Lesson Complete!",
    "lesson.completedDesc": "Wonderful work! You've finished this lesson.",
    "lesson.related": "Related lessons",
    "lesson.next": "Next lesson",
    "lesson.notFound": "Lesson not found.",
    "lesson.section": "Section",

    "play.title": "Learning Games",
    "play.subtitle": "Six playable games with real scoring and instant feedback. No tokens, no entry fees — just fun.",
    "play.start": "Start Game",
    "play.chooseDifficulty": "Choose difficulty",
    "play.summary": "Game Summary",
    "play.finalScore": "Final score",
    "play.question": "Question",
    "play.of": "of",

    "stories.title": "Stories & Resources",
    "stories.subtitle": "Gentle, read-along stories and printable learning sheets for parents and teachers.",
    "stories.readingProgress": "Reading progress",
    "stories.printSheet": "Print activity sheet",
    "stories.parentResources": "Parent & Teacher Resources",
    "stories.resourcesDesc": "Printable sheets you can use at home or in the classroom.",
    "stories.awaitingTranslation": "Awaiting translation",
    "stories.notFound": "Story not found.",
    "stories.page": "Page",
    "stories.finish": "Finish Story",

    "impact.title": "Impact Dashboard",
    "impact.subtitle": "How LUMORA intends to support free education through transparent, verifiable charitable contributions.",
    "impact.prelaunch.title": "Pre-Launch State",
    "impact.prelaunch.desc": "The token contract, treasury, and on-chain indexer are not yet deployed. No live donation metrics are shown. We will never display fake amounts, fake transaction hashes, or an invented number of children helped.",
    "impact.statuses": "Contribution stages",
    "impact.projects": "Projects",
    "impact.donations": "Donation Records",
    "impact.noDonations": "No confirmed or pending donations yet. Records will appear here only when they occur on-chain.",
    "impact.treasury": "Treasury",
    "impact.treasuryAddr": "Treasury address",
    "impact.confirmedTotal": "Confirmed donations total",
    "impact.childrenHelped": "Children helped",
    "impact.reports": "Impact Reports & Methodology",
    "impact.recipient": "Recipient",
    "impact.region": "Region",
    "impact.verified": "Recipient verified",
    "impact.unverified": "Recipient not yet verified",

    "about.title": "About LUMORA",
    "about.mission.title": "Our Educational Mission",
    "about.mission.desc": "LUMORA exists to make learning free, joyful, and accessible to every child and family. We believe a brighter future starts with education — and that playing and giving are part of how we learn.",
    "about.why.title": "Why Free Learning Matters",
    "about.why.desc": "Access to good education shouldn't depend on where you live or what you can afford. LUMORA keeps the core learning experience free and open, no account or wallet required.",
    "about.philosophy.title": "Learn, Play, Give",
    "about.community.title": "The Role of Community",
    "about.community.desc": "Our community of learners, families, and volunteers helps shape lessons, stories, and the mission itself.",
    "about.roadmap.title": "Roadmap",
    "about.tech.title": "Education & Responsible Technology",
    "about.tech.desc": "We introduce technology — including blockchain concepts — thoughtfully and age-appropriately, always prioritizing safety and understanding.",
    "about.giggle.title": "Our Relationship with Giggle Academy",
    "about.giggle.desc": "LUMORA is an independent initiative inspired by the goal of making education more accessible. We aim to support Giggle Academy's educational mission through transparent charitable contributions, subject to recipient verification and the applicable donation process. A donation or contribution does not establish an official partnership. LUMORA is not officially partnered with, endorsed by, owned by, or operated by Giggle Academy.",

    "token.title": "Token Information",
    "token.overview": "Project Overview",
    "token.overviewDesc": "The LUMORA token is planned to help fund free education through a transparent charity mechanism. Participation in any token is separate from — and never required for — access to learning.",
    "token.network": "Network",
    "token.contract": "Contract Address",
    "token.tokenomics": "Tokenomics",
    "token.charity": "Charity Mechanism",
    "token.charityDesc": "If a finalized token contract implements a tax, that revenue could fund verified charitable contributions. This mechanism is not yet live and no tax is currently collected.",
    "token.buy": "Buy on Flap",
    "token.chart": "View Chart",
    "token.chartNote": "This is a general Dexscreener link, not a verified LUMORA trading chart. It will be updated when the real token pair is available.",
    "token.risk": "Risk Disclosure",
    "token.riskDesc": "Tokens involve significant financial risk, including possible total loss. Nothing here is financial advice. Token participation is entirely separate from the free education LUMORA provides.",
    "token.audit": "Audit status",
    "token.official": "Official X account",

    "community.title": "Community",
    "community.mission": "Community Mission",
    "community.missionDesc": "LUMORA's community believes education should be free and joyful. We grow through shared learning, kindness, and voluntary participation.",
    "community.challenges": "Educational Challenges",
    "community.milestones": "Learning Milestones",
    "community.contributions": "Voluntary Community Contributions",
    "community.records": "Publicly Verifiable Donation Records",
    "community.guidelines": "Participation Guidelines",
    "community.guidelinesDesc": "Be kind, be curious, keep it safe and age-appropriate, and help others learn.",
    "community.noFake": "We do not publish member counts, testimonials, or activity we cannot verify.",

    "faq.title": "Frequently Asked Questions",
    "faq.subtitle": "Everything about learning, the mission, and the token.",

    "legal.title": "Legal & Privacy",
    "legal.privacy": "Privacy Policy",
    "legal.terms": "Terms of Use",
    "legal.disclaimer": "Educational Disclaimer",
    "legal.risk": "Token Risk Disclosure",
    "legal.methodology": "Charity Transparency Methodology",
    "legal.copyright": "Content & Copyright Policy",

    "footer.tagline": "A Brighter Future Starts With Education.",
    "footer.explore": "Explore",
    "footer.mission": "Mission",
    "footer.legal": "Legal",
    "footer.follow": "Follow us",
    "footer.xPlaceholder": "Official X — coming soon",
    "footer.rights": "LUMORA. An independent educational initiative.",
    "footer.wallet": "No wallet required to learn.",
  },

  zh: {
    "brand.tagline": "更光明的未来，从教育开始",
    "brand.support": "在学习、创造与社区力量中，探索更美好的世界。",

    "nav.home": "首页",
    "nav.learn": "学习中心",
    "nav.play": "趣味游戏",
    "nav.stories": "故事天地",
    "nav.impact": "公益影响",
    "nav.about": "关于我们",
    "nav.token": "代币",
    "nav.community": "社区",
    "nav.faq": "常见问题",
    "nav.startLearning": "开始学习",
    "nav.buyToken": "购买代币",
    "nav.language": "语言",
    "nav.menu": "菜单",

    "common.startLesson": "开始课程",
    "common.continue": "继续",
    "common.play": "开始",
    "common.playNow": "立即游玩",
    "common.readStory": "阅读故事",
    "common.search": "搜索",
    "common.searchPlaceholder": "搜索课程……",
    "common.all": "全部",
    "common.category": "分类",
    "common.difficulty": "难度",
    "common.sortBy": "排序",
    "common.age": "年龄",
    "common.duration": "时长",
    "common.min": "分钟",
    "common.completed": "已完成",
    "common.inProgress": "进行中",
    "common.notStarted": "未开始",
    "common.back": "返回",
    "common.next": "下一个",
    "common.previous": "上一个",
    "common.comingSoon": "敬请期待",
    "common.notVerified": "尚未核实",
    "common.notAvailable": "尚未提供",
    "common.learnMore": "了解更多",
    "common.viewAll": "查看全部",
    "common.noResults": "未找到结果，请尝试其他搜索或筛选。",
    "common.restart": "再玩一次",
    "common.score": "得分",
    "common.correct": "回答正确！",
    "common.incorrect": "还差一点，再试试！",
    "common.yourBest": "你的最佳成绩",
    "common.difficultyEasy": "简单",
    "common.difficultyMedium": "中等",
    "common.difficultyHard": "困难",
    "common.openInNewTab": "在新标签页打开",

    "home.hero.title": "更光明的未来，从教育开始",
    "home.hero.subtitle": "欢迎来到 LUMORA——一个充满魔法的世界，孩子和家庭在这里一起学习、游玩，并共同建设更友善、更光明的未来。免费探索，无需钱包。",
    "home.cta.start": "开始学习",
    "home.cta.impact": "了解公益影响",
    "home.philosophy.title": "学习 • 游玩 • 奉献",
    "home.philosophy.learn": "学习",
    "home.philosophy.learnDesc": "七大有趣主题，生动易懂、人人可及的课程。",
    "home.philosophy.play": "游玩",
    "home.philosophy.playDesc": "寓教于乐的互动游戏，让学习成为享受。",
    "home.philosophy.give": "奉献",
    "home.philosophy.giveDesc": "透明、可验证的慈善捐助——绝无虚假数据。",
    "home.categories.title": "精选学习分类",
    "home.categories.subtitle": "选择一条路径，开始探索真实、动手的课程。",
    "home.preview.title": "走进一节课",
    "home.preview.subtitle": "每节课都有清晰的目标、简短易读的内容，以及友好的知识小测。",
    "home.games.title": "精选游戏",
    "home.games.subtitle": "轻松有趣的挑战，培养真实能力。",
    "home.stories.title": "精选故事",
    "home.stories.subtitle": "温柔的故事，教会孩子善良、好奇与勇气。",
    "home.community.title": "社区驱动的使命",
    "home.community.desc": "LUMORA 因学习者、家庭和志愿者而成长，他们相信教育应当对每个人都免费而快乐。",
    "home.impact.title": "以透明为设计原则",
    "home.impact.desc": "我们只展示能够证明的内容。慈善捐助会经过清晰的阶段——计划中、待处理、已确认——上线后每一笔都可在链上追溯。",
    "home.giggle.title": "支持 Giggle Academy 的使命",
    "home.giggle.desc": "LUMORA 是一项独立倡议，受到让教育更可及这一目标的启发。我们希望通过透明的慈善捐助支持 Giggle Academy 的教育使命，但须经接收方核实并遵循相应的捐助流程。",
    "home.resources.title": "最新教育资源",
    "home.resources.subtitle": "马上就能探索的新课程与故事。",

    "learn.title": "学习中心",
    "learn.subtitle": "免费、可搜索的课程库，涵盖七大主题的动手课程。",
    "learn.lessonsCount": "{n} 节课程",
    "learn.sort.recommended": "推荐",
    "learn.sort.shortest": "时长最短",
    "learn.sort.az": "按字母排序",
    "learn.objectives": "你将学到",

    "lesson.objectives": "学习目标",
    "lesson.progress": "进度",
    "lesson.check": "知识小测",
    "lesson.checkPrompt": "请选择最佳答案：",
    "lesson.markComplete": "标记为完成",
    "lesson.completedTitle": "课程完成！",
    "lesson.completedDesc": "太棒了！你已经完成了这节课。",
    "lesson.related": "相关课程",
    "lesson.next": "下一节课",
    "lesson.notFound": "未找到该课程。",
    "lesson.section": "第",

    "play.title": "趣味游戏",
    "play.subtitle": "六款可玩的游戏，有真实计分和即时反馈。没有代币，没有入场费，只有快乐。",
    "play.start": "开始游戏",
    "play.chooseDifficulty": "选择难度",
    "play.summary": "游戏总结",
    "play.finalScore": "最终得分",
    "play.question": "第",
    "play.of": "题，共",

    "stories.title": "故事与资源",
    "stories.subtitle": "温柔的伴读故事，以及给家长和老师的可打印学习单。",
    "stories.readingProgress": "阅读进度",
    "stories.printSheet": "打印活动单",
    "stories.parentResources": "家长与教师资源",
    "stories.resourcesDesc": "可在家中或课堂使用的可打印学习单。",
    "stories.awaitingTranslation": "等待翻译中",
    "stories.notFound": "未找到该故事。",
    "stories.page": "第",
    "stories.finish": "读完故事",

    "impact.title": "公益影响面板",
    "impact.subtitle": "LUMORA 计划如何通过透明、可验证的慈善捐助支持免费教育。",
    "impact.prelaunch.title": "上线前状态",
    "impact.prelaunch.desc": "代币合约、金库和链上索引器尚未部署。不会显示任何实时捐助数据。我们绝不会展示虚假金额、虚假交易哈希或虚构的受助儿童人数。",
    "impact.statuses": "捐助阶段",
    "impact.projects": "项目",
    "impact.donations": "捐助记录",
    "impact.noDonations": "暂无已确认或待处理的捐助。只有在链上发生时，记录才会显示在此。",
    "impact.treasury": "金库",
    "impact.treasuryAddr": "金库地址",
    "impact.confirmedTotal": "已确认捐助总额",
    "impact.childrenHelped": "受助儿童人数",
    "impact.reports": "影响报告与方法论",
    "impact.recipient": "接收方",
    "impact.region": "地区",
    "impact.verified": "接收方已核实",
    "impact.unverified": "接收方尚未核实",

    "about.title": "关于 LUMORA",
    "about.mission.title": "我们的教育使命",
    "about.mission.desc": "LUMORA 致力于让学习对每个孩子和家庭都免费、快乐、可及。我们相信更光明的未来始于教育——而游玩与奉献正是我们学习的一部分。",
    "about.why.title": "为什么免费学习很重要",
    "about.why.desc": "获得优质教育不应取决于你住在哪里或负担得起什么。LUMORA 让核心学习体验保持免费开放，无需账号或钱包。",
    "about.philosophy.title": "学习、游玩、奉献",
    "about.community.title": "社区的角色",
    "about.community.desc": "由学习者、家庭和志愿者组成的社区帮助塑造课程、故事以及使命本身。",
    "about.roadmap.title": "路线图",
    "about.tech.title": "教育与负责任的技术",
    "about.tech.desc": "我们以审慎、适龄的方式介绍技术——包括区块链概念——始终把安全与理解放在首位。",
    "about.giggle.title": "我们与 Giggle Academy 的关系",
    "about.giggle.desc": "LUMORA 是一项独立倡议，受到让教育更可及这一目标的启发。我们希望通过透明的慈善捐助支持 Giggle Academy 的教育使命，但须经接收方核实并遵循相应的捐助流程。捐助或贡献并不构成正式合作关系。LUMORA 与 Giggle Academy 之间不存在官方合作、背书、所有或运营关系。",

    "token.title": "代币信息",
    "token.overview": "项目概览",
    "token.overviewDesc": "LUMORA 代币计划通过透明的慈善机制帮助资助免费教育。参与任何代币与获取学习资源是分开的，且从不作为前提条件。",
    "token.network": "网络",
    "token.contract": "合约地址",
    "token.tokenomics": "代币经济",
    "token.charity": "慈善机制",
    "token.charityDesc": "如果最终确定的代币合约实现了税费，该收入可用于资助已核实的慈善捐助。该机制尚未上线，目前不收取任何税费。",
    "token.buy": "在 Flap 购买",
    "token.chart": "查看图表",
    "token.chartNote": "这是一个通用的 Dexscreener 链接，并非经过验证的 LUMORA 交易图表。真实代币交易对可用后将会更新。",
    "token.risk": "风险提示",
    "token.riskDesc": "代币涉及重大财务风险，包括可能的全部损失。此处内容均非投资建议。参与代币与 LUMORA 提供的免费教育完全分开。",
    "token.audit": "审计状态",
    "token.official": "官方 X 账号",

    "community.title": "社区",
    "community.mission": "社区使命",
    "community.missionDesc": "LUMORA 社区相信教育应当免费而快乐。我们在共同学习、善意与自愿参与中成长。",
    "community.challenges": "教育挑战",
    "community.milestones": "学习里程碑",
    "community.contributions": "自愿的社区贡献",
    "community.records": "公开可验证的捐助记录",
    "community.guidelines": "参与指南",
    "community.guidelinesDesc": "保持友善、好奇、安全与适龄，并帮助他人学习。",
    "community.noFake": "我们不会发布无法验证的成员数量、评价或活动。",

    "faq.title": "常见问题",
    "faq.subtitle": "关于学习、使命与代币的一切。",

    "legal.title": "法律与隐私",
    "legal.privacy": "隐私政策",
    "legal.terms": "使用条款",
    "legal.disclaimer": "教育免责声明",
    "legal.risk": "代币风险提示",
    "legal.methodology": "慈善透明度方法论",
    "legal.copyright": "内容与版权政策",

    "footer.tagline": "更光明的未来，从教育开始。",
    "footer.explore": "探索",
    "footer.mission": "使命",
    "footer.legal": "法律",
    "footer.follow": "关注我们",
    "footer.xPlaceholder": "官方 X——敬请期待",
    "footer.rights": "LUMORA。一项独立的教育倡议。",
    "footer.wallet": "学习无需钱包。",
  },
};

const LangContext = createContext({ param: "en", code: "en", t: (k) => k, lp: (p) => p });

export function LanguageProvider({ param, children }) {
  const code = paramToCode(param);

  useEffect(() => {
    document.documentElement.lang = param;
    try {
      localStorage.setItem(LANG_KEY, param);
    } catch {}
  }, [param]);

  const value = useMemo(() => {
    const t = (key, vars) => {
      let s = dict[code]?.[key] ?? dict.en[key] ?? key;
      if (vars) Object.keys(vars).forEach((k) => (s = s.replace(`{${k}}`, vars[k])));
      return s;
    };
    const lp = (path) => `/${param}${path === "/" ? "" : path}`;
    return { param, code, t, lp };
  }, [param, code]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  return useContext(LangContext);
}

// Pick localized field from a {en, zh} object or a "field_en"/"field_zh" pair.
export function pick(obj, code, base) {
  if (!obj) return "";
  if (base) return obj[`${base}_${code}`] ?? obj[`${base}_en`] ?? "";
  return obj[code] ?? obj.en ?? "";
}
